import axios from 'axios'
import { ACCESS_TOKEN, REFRESH_TOKEN, API_BASE_URL, LOGIN_DATA } from './apiConstants'
import { createLimiter } from '../../utils/requestLimiter'

// Base url instance
const instance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 1000000
})

// Limit concurrent flight search requests to 4.
// Chrome 155 keeps the original request's socket while waiting for the CORS
// preflight of each redirect hop (POST -> 303 -> GET -> 307 -> GET). With 6+
// searches in flight against an HTTP/1.1 host, all 6 sockets are held and the
// preflights starve, deadlocking every request. Remove this once the API is
// served over HTTP/2 or same-origin.
const SEARCH_URL_MARKER = 'pre_flight_search' // also matches pre_flight_search_inv
const MAX_CONCURRENT_SEARCHES = 4
const searchLimiter = createLimiter(MAX_CONCURRENT_SEARCHES)
const RELEASE_KEY = '__releaseSearchSlot'

const releaseSearchSlot = config => {
  const release = config && config[RELEASE_KEY]
  if (release) {
    config[RELEASE_KEY] = null
    release()
  }
}

instance.interceptors.request.use(async config => {
  if (config.url && config.url.includes(SEARCH_URL_MARKER)) {
    config[RELEASE_KEY] = await searchLimiter.acquire()
  }
  return config
})

instance.interceptors.response.use(
  response => {
    releaseSearchSlot(response.config)
    return response
  },
  error => {
    releaseSearchSlot(error && error.config)
    return Promise.reject(error)
  }
)

// function to refresh token
const refreshTokens = async (token, userId) => {
  // console.log("token------------",token)
  let newRes
  try {
    newRes = await instance.post('/login/refresh_token', { refresh_token: token, user_id: userId }) // token.refreshToken
    localStorage.setItem(ACCESS_TOKEN, newRes.data.token.access_token)
    localStorage.setItem(REFRESH_TOKEN, newRes.data.token.refresh_token)
  } catch (error) {
    newRes = null
  }

  return newRes
}

// function to remove localstorage data
const sessionExpired = () => {
  localStorage.clear()
  return (window.location.href = '/login')
}

// function to define get request
export const getAuth = async (url, params) => {
  const axiosConfig = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  }
  const token = localStorage.getItem(ACCESS_TOKEN) ? localStorage.getItem(ACCESS_TOKEN) : {}
  const refreshToken = localStorage.getItem(REFRESH_TOKEN) ? localStorage.getItem(REFRESH_TOKEN) : {}
  const userId = localStorage.getItem(LOGIN_DATA) ? JSON.parse(localStorage.getItem(LOGIN_DATA)).data.user_id : {}
  axiosConfig.headers.Authorization = token
  const options = {
    ...axiosConfig,
    ...params
  }

  // console.log'options=========>',options);
  const res = await instance.get(url, options)
  // console.log(res.data.msg);
  let respo
  if (res.data.msg === 'Invalid Token' || res.data.msg === 'Signature verification failed') {
    return sessionExpired()
  }
  if (res.data.msg === 'Token Expired') {
    const newRes = await refreshTokens(refreshToken, userId)

    // console.log('newRes--------------->',newRes.data.token.access_token);

    // console.log(newRes);

    if (newRes) {
      axiosConfig.headers.Authorization = newRes.data.token.access_token
      const options = {
        ...axiosConfig,
        ...params
      }

      const response = await instance.get(url, options)

      // console.log('response-------->',response);

      if (response.data.msg === 'Invalid Token') {
        return sessionExpired()
      }
      respo = response
    } else {
      respo = null
      return sessionExpired()
    }
  } else {
    respo = res
  }
  // console.log('respo---------------->',respo);
  return respo
}

// function to handle authorization post request
export const authPost = async (url, data) => {
  const axiosConfig = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  }
  const token = localStorage.getItem(ACCESS_TOKEN) ? localStorage.getItem(ACCESS_TOKEN) : {}
  const refreshToken = localStorage.getItem(REFRESH_TOKEN) ? localStorage.getItem(REFRESH_TOKEN) : {}
  const userId = localStorage.getItem(LOGIN_DATA) ? JSON.parse(localStorage.getItem(LOGIN_DATA)).data.user_id : {}
  axiosConfig.headers.Authorization = token
  const options = {
    ...axiosConfig
  }

  let res
  res = await instance.post(url, data, options)
  console.log(res.data.msg)
  if (res.data.msg === 'Invalid Token' || res.data.msg === 'Signature verification failed') {
    return sessionExpired()
  }
  if (res.data.msg === 'Token Expired') {
    const newRes = await refreshTokens(refreshToken, userId)

    axiosConfig.headers.Authorization = newRes.data.token.access_token

    res = await instance.post(url, data, axiosConfig)
    if (res.data.msg === 'Invalid Token' || res.data.msg === 'Signature verification failed') {
      return sessionExpired()
    }
  }
  return res
}

// function to handle post request
export const post = async (url, data) => {
  const axiosConfig = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  }
  const res = await instance.post(url, data, axiosConfig)
  return res
}

// function to handle login post request
export const login = async (url, data) => {
  const axiosConfig = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  }
  const res = await instance.post(url, data, axiosConfig)
  return res
}

export const getReq = async (url, data) => {
  const axiosConfig = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  }
  const res = await instance.get(url, data, axiosConfig)
  return res
}

export const authPostDownload = async (url, data) => {
  const axiosConfig = {
    headers: {
      Accept: 'application/pdf, application/octet-stream',
      'Content-Type': 'application/json'
    },
    responseType: 'blob' // responseType should be at config level, not in headers
  }
  const token = localStorage.getItem(ACCESS_TOKEN) ? localStorage.getItem(ACCESS_TOKEN) : {}
  const refreshToken = localStorage.getItem(REFRESH_TOKEN) ? localStorage.getItem(REFRESH_TOKEN) : {}
  const userId = localStorage.getItem(LOGIN_DATA) ? JSON.parse(localStorage.getItem(LOGIN_DATA)).data.user_id : {}
  axiosConfig.headers.Authorization = token
  const options = {
    ...axiosConfig
  }

  let res
  res = await instance.post(url, data, options)
  
  // For blob responses, we can't check res.data.msg, so skip token validation on blob responses
  // The token should be valid before calling this function
  return res
}
